import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, TextField, useTheme, InputAdornment } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarToday as CalendarIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const DatePicker = ({ value, onChange, placeholder = 'Select date' }: DatePickerProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const [monthRef, setMonthRef] = useState(value ? dayjs(value).startOf('month') : dayjs().startOf('month'));
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (open && rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 320;

      // Position dropdown above if not enough space below
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPosition({
          top: rect.top - dropdownHeight - 8,
          left: rect.left,
        });
      } else {
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
        });
      }
    }
  }, [open]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (
        rootRef.current &&
        dialogRef.current &&
        !rootRef.current.contains(e.target as Node) &&
        !dialogRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setDropdownPosition(null);
      }
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const onDayClick = (day: Dayjs) => {
    onChange(day.format('YYYY-MM-DD'));
    setOpen(false);
    setDropdownPosition(null);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  // Generate calendar days
  const generateDays = () => {
    const firstDay = monthRef.startOf('month');
    const offset = firstDay.day(); // 0 = Sunday
    const gridStart = firstDay.subtract(offset, 'day');
    return Array.from({ length: 42 }, (_, i) => gridStart.add(i, 'day'));
  };

  const days = generateDays();
  const selectedDate = value ? dayjs(value) : null;

  return (
    <Box ref={rootRef} sx={{ position: 'relative', width: '100%' }}>
      <TextField
        size="small"
        value={value || ''}
        onClick={() => setOpen(!open)}
        placeholder={placeholder}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {value ? (
                <IconButton size="small" onClick={handleClear} sx={{ p: 0.25 }}>
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              ) : (
                <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          width: '100%',
          '& .MuiInputBase-root': {
            fontSize: '0.75rem',
            bgcolor: 'white',
            cursor: 'pointer',
          },
          '& .MuiInputBase-input': {
            py: 0.5,
            px: 1,
            cursor: 'pointer',
          },
        }}
      />

      {open &&
        dropdownPosition &&
        createPortal(
          <Box
            ref={dialogRef}
            sx={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 99999,
              width: 280,
              borderRadius: '8px',
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: '#fff',
              boxShadow: '0 8px 20px rgba(146,153,184,0.15)',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: `1px solid ${theme.palette.divider}`,
                p: 1.5,
              }}
            >
              <IconButton
                size="small"
                onClick={() => setMonthRef(monthRef.subtract(1, 'month'))}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '6px',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: `${theme.palette.primary.main}08`,
                  },
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Box sx={{ fontWeight: 600, fontSize: '14px' }}>
                {monthRef.format('MMMM YYYY')}
              </Box>
              <IconButton
                size="small"
                onClick={() => setMonthRef(monthRef.add(1, 'month'))}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '6px',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: `${theme.palette.primary.main}08`,
                  },
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Calendar */}
            <Box sx={{ p: 1.5 }}>
              {/* Weekday headers */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: 0.5,
                  textAlign: 'center',
                  mb: 0.5,
                  fontSize: 11,
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                }}
              >
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                  <Box key={d}>{d}</Box>
                ))}
              </Box>

              {/* Days */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                {days.map((day, idx) => {
                  const inMonth = day.month() === monthRef.month();
                  const isSelected = selectedDate && day.isSame(selectedDate, 'day');
                  const isToday = day.isSame(dayjs(), 'day');

                  return (
                    <Box
                      key={idx}
                      onClick={() => onDayClick(day)}
                      sx={{
                        minWidth: 0,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '6px',
                        fontSize: 12,
                        fontWeight: isSelected || isToday ? 600 : 400,
                        backgroundColor: isSelected
                          ? theme.palette.primary.main
                          : 'transparent',
                        color: isSelected
                          ? '#fff'
                          : inMonth
                          ? theme.palette.text.primary
                          : theme.palette.text.disabled,
                        cursor: 'pointer',
                        border: isToday && !isSelected ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? theme.palette.primary.dark
                            : `${theme.palette.primary.main}10`,
                        },
                      }}
                    >
                      {day.date()}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>,
          document.body
        )}
    </Box>
  );
};

export default DatePicker;
